/**
 * What a chain verification request should actually ask for.
 *
 * Extracted as a pure function for one reason: it was wrong, invisibly, for as long as the
 * page has existed, and nothing could have caught it.
 *
 * The Audit Trail page renders a "Scoped to agent <id>" lock chip, passes that agent to the
 * table query, and then called verify with only `hours` — so the verdict under the chip
 * walked the whole TENANT chain. Meanwhile the agent detail page passed `agentId` and
 * walked the AGENT chain. Observed live on one agent at one instant: "Chain verified · 2484
 * events" on one screen, "2 breaks across 14,548 events" on the other. Both correct, about
 * different chains, neither saying which.
 *
 * The batch arithmetic inherited the same confusion. Slice count came from the
 * agent-filtered `totalElements` while each slice verified the tenant chain, so 23 slices
 * sized for ~2,300 events each carried ~630.
 *
 * A component test could assert this, but every one of this codebase's existing frontend
 * tests is over pure logic. So the decision moves out of the handler and becomes testable
 * on its own terms.
 */

/** Matches the backend's UUID parameter binding; anything else is not a scope. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isScopedToAgent = (agentId) =>
  typeof agentId === 'string' && UUID_RE.test(agentId.trim());

/**
 * Build the argument object for `api.verifyAuditIntegrity`.
 *
 * @param {object}  opts
 * @param {number}  opts.hours        window the page is showing
 * @param {string}  [opts.agentId]    the scope chip's agent, when one is applied
 * @param {string}  [opts.fromInstant] batched path: slice lower bound
 * @param {string}  [opts.toInstant]   batched path: slice upper bound
 * @param {AbortSignal} [opts.signal]
 * @returns {object} argument object — always an object, never the bare-number form, so the
 *                   scoped and unscoped paths cannot diverge in shape
 */
export const buildVerifyArgs = ({ hours, agentId, fromInstant, toInstant, signal } = {}) => {
  const args = { hours };
  // The verdict must answer for the scope the page is displaying. A chip that says
  // "scoped to agent" above a tenant-wide verdict is a false statement about evidence,
  // which on a compliance surface is worse than showing no verdict at all.
  if (isScopedToAgent(agentId)) args.agentId = agentId.trim();
  if (fromInstant) args.fromInstant = fromInstant;
  if (toInstant) args.toInstant = toInstant;
  if (signal) args.signal = signal;
  return args;
};

/**
 * Which chain a verdict is about, for display.
 *
 * Prefers the backend's own `scopeLabel` — it is the authority on what it walked, and a
 * frontend that infers the scope from its own request would keep agreeing with itself
 * while disagreeing with the server. The local inference is a fallback for a response
 * predating the field.
 */
export const chainLabelOf = (integrity, agentId) => {
  if (integrity && typeof integrity.scopeLabel === 'string' && integrity.scopeLabel) {
    return integrity.scopeLabel;
  }
  if (integrity && integrity.scope === 'AGENT') return 'Agent chain';
  if (integrity && integrity.scope === 'TENANT') return 'Tenant chain';
  return isScopedToAgent(agentId) ? 'Agent chain' : 'Tenant chain';
};

/**
 * How a verdict should be presented.
 *
 * A chain can be invalid because rows were altered, or because they were appended out of
 * order. Both are invalid; only one is a security event. Rendering them identically is
 * what sent a race condition to an operator as "BREAK DETECTED — investigate immediately"
 * and cost hours of forensic work on evidence that turned out to be intact.
 *
 * @returns {{tone: 'success'|'danger'|'warning', title: string, canRepair: boolean}}
 */
/**
 * Split the breaks into rows that can no longer be checked and everything else.
 *
 * Accepts both the server's shape (`kind`) and the adapted shape (`breakKind`). The caller
 * today passes the raw response, but this function decides what an operator is told, and
 * having it quietly return "nothing to see" because it was handed the other shape is the
 * kind of failure that only shows up on the day it matters.
 */
export const countBreakKinds = (integrity) => {
  const breaks = Array.isArray(integrity && integrity.breaks) ? integrity.breaks : [];
  let unverifiable = 0;
  let other = 0;
  for (const b of breaks) {
    const kind = b && (b.kind || b.breakKind);
    if (kind === 'FORMULA_UNRECOVERABLE') unverifiable += 1;
    else other += 1;
  }
  return { unverifiable, other };
};

const records = (n) => (n === 1 ? 'record' : 'records');

/**
 * The one answer to "what kind of invalid is this chain".
 *
 * AuditHashIntegrity derived this itself — `contentUnaltered ? 'outOfOrder' : 'broken'` —
 * while this file derived it again for the Audit Trail banner. Two derivations of one
 * concept, which is the defect this codebase keeps paying for: when a third outcome
 * appeared, one of them learned about it and the other kept confidently rendering the old
 * sentence. Both now ask here.
 *
 * - `valid`         nothing disagrees
 * - `broken`        at least one record's own hash disagrees with its own contents
 * - `unverifiable`  nothing was altered, but some records can no longer be checked at all
 * - `outOfOrder`    nothing was altered and everything is checkable; the sequence is wrong
 *
 * @returns {'unknown'|'valid'|'broken'|'unverifiable'|'outOfOrder'}
 */
export const chainState = (integrity) => {
  if (!integrity) return 'unknown';
  if (integrity.intact) return 'valid';
  // Anything tamper-shaped outranks everything else: one altered record in a walk full of
  // benign ones is still an altered record, and must not be softened by its neighbours.
  if (integrity.contentUnaltered !== true) return 'broken';
  return countBreakKinds(integrity).unverifiable > 0 ? 'unverifiable' : 'outOfOrder';
};

export const verdictPresentation = (integrity, agentId) => {
  const chain = chainLabelOf(integrity, agentId);
  if (!integrity) return { tone: 'muted', title: `${chain} — not verified`, canRepair: false };
  if (integrity.intact) return { tone: 'success', title: `${chain} intact`, canRepair: false };

  // Which kind of invalid this is has one owner; this function only decides how to say it.
  const state = chainState(integrity);

  if (state === 'unverifiable') {
    const { unverifiable, other } = countBreakKinds(integrity);
    // Rows sealed under a retired formula are not out of order, and saying so would be a
    // second wrong sentence replacing the first. They are rows nobody can check — neither
    // "intact" nor "altered", and the honest third thing.
    return {
      tone: 'warning',
      title: other > 0
        ? `${chain} out of order, and ${unverifiable} earlier ${records(unverifiable)} can no longer be checked`
        : `${chain} — ${unverifiable} earlier ${records(unverifiable)} can no longer be checked`,
      // Never, even if the server says fullyRepairable. A rebuild here would seal today's
      // content under today's formula and render the chain green having checked nothing.
      canRepair: false,
    };
  }

  if (state === 'outOfOrder') {
    return {
      tone: 'warning',
      title: `${chain} out of order — content intact`,
      canRepair: integrity.fullyRepairable === true,
    };
  }

  return {
    tone: 'danger',
    title: `${chain} — altered records detected`,
    // Never offer a rebuild here. It recomputes hashes from whatever the rows now say,
    // which would make an edit permanent and self-consistent.
    canRepair: false,
  };
};

/**
 * Map a structured break from the server into the shape the forensic modal already reads,
 * without losing what makes it structured.
 *
 * The modal consumes `kind: 'chain' | 'hash'`, `position`, `expectedHash`, `actualHash` —
 * a vocabulary invented when breaks arrived as prose and had to be regex-parsed back into
 * fields. Rather than rewrite the modal and the parser in the same change, the server's
 * richer kinds are mapped onto that vocabulary and carried alongside it, so the existing
 * rendering keeps working while anything that wants the real classification can read
 * `breakKind`, `severity` and `repairable`.
 */
const LEGACY_KIND = {
  LINK_MISMATCH: 'chain',
  FORK: 'chain',
  OWN_HASH_MISMATCH: 'hash',
  FORMULA_DRIFT: 'hash',
  NOT_MATERIALIZED: 'other',
  ORPHAN: 'other',
  // Not 'hash'. The modal renders a 'hash' break as a stored-versus-recomputed comparison,
  // and for this kind there is no meaningful recomputed value to compare against — the
  // server sends none, deliberately.
  FORMULA_UNRECOVERABLE: 'other',
};

export const adaptBreak = (b, idx) => ({
  idx,
  kind: LEGACY_KIND[b.kind] || 'other',
  breakKind: b.kind,
  severity: b.severity,
  repairable: b.repairable === true,
  eventId: b.eventId,
  position: typeof b.index === 'number' ? b.index : undefined,
  expectedHash: b.expected,
  actualHash: b.found,
  message: b.message,
});

/**
 * True when the server sent structured breaks. Used to decide whether to adapt them or
 * fall back to parsing prose — never to decide whether a chain is valid.
 */
export const hasStructuredBreaks = (data) =>
  Array.isArray(data && data.breaks) && data.breaks.length > 0;
